// Google Calendar OAuth integration utility
// Handles the complete OAuth flow for calendar access

// Google types are now centrally declared in types/google.d.ts

export async function connectGoogleCalendar(): Promise<void> {
    // Ensure Google Identity Services is loaded
    if (!window.google?.accounts?.oauth2) {
    throw new Error('Google Identity Services not loaded');
    }

    // Get client ID from environment
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
    throw new Error('Google Client ID not configured');
    }

  // Initialize OAuth client with popup mode
  // Note: In popup mode, Google Identity Services automatically uses the origin as redirect URI
  // The origin (e.g., https://community.superkonnected.nl) must be in Google Cloud Console
  // For token exchange, Google Identity Services uses 'postmessage' automatically
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: [
        "openid",
        "email", 
        "profile",
        "https://www.googleapis.com/auth/calendar.events",
      ].join(" "),
    ux_mode: "popup", // Use popup mode - callback will be called directly
      callback: async (response: { code?: string }) => {
        if (!response.code) {
        console.error('No authorization code received');
          return;
        }

        try {
          console.log('🔑 Google auth code received:', response.code.substring(0, 20) + '...');
          
        // Call Firebase Cloud Function with the code
        // Note: For popup mode, we use 'postmessage' as redirect URI in token exchange
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions(undefined, 'europe-west1');
          const linkGoogleAccount = httpsCallable(functions, 'linkGoogleAccount');
          
          console.log('📞 Calling Firebase Cloud Function linkGoogleAccount...');
        const result = await linkGoogleAccount({ 
          code: response.code
          // Don't pass redirectUri - Firebase Function uses 'postmessage' for popup mode
        });
          
          const data = result.data as { connected?: boolean; ok?: boolean; success?: boolean; message?: string; error?: string };
          
        // Handle multiple response formats
          if (data.connected || data.ok || data.success) {
            console.log('✅ Google Calendar connection successful');
          // Trigger success event for UI update
          window.dispatchEvent(new CustomEvent('googleCalendarConnected', { detail: data }));
          } else {
          throw new Error(data.error || data.message || 'Failed to connect Google Calendar');
          }
        } catch (error: any) {
          console.error('❌ Error in Google Calendar connection:', error);
        // Trigger error event for UI update
        window.dispatchEvent(new CustomEvent('googleCalendarError', { 
          detail: error.message || 'Network error while connecting Google Calendar' 
        }));
        }
      },
    });

  // Store return URL for after OAuth completes
  sessionStorage.setItem('googleCalendarReturnUrl', window.location.hash || '#agenda');
  
  // Start the OAuth flow - this will redirect the page
    client.requestCode();
  
  // Note: This function doesn't return a promise that resolves
  // because the page will redirect. The result is handled in the callback page.
}

// Load Google Identity Services script (now loaded globally in index.html)
export function loadGoogleIdentityServices(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded (script is now loaded globally in index.html)
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }

    // Wait for the global script to load (with timeout)
    let attempts = 0;
    const maxAttempts = 50; // 5 seconds timeout
    
    const checkLoaded = () => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }
      
      attempts++;
      if (attempts >= maxAttempts) {
        reject(new Error('Google Identity Services failed to load within timeout. Check network connection.'));
        return;
      }
      
      setTimeout(checkLoaded, 100);
    };
    
    checkLoaded();
  });
}

// Update user's integration status in Firestore
export async function updateCalendarIntegrationStatus(userId: string, connected: boolean): Promise<void> {
  const { doc, updateDoc } = await import('firebase/firestore');
  const { db } = await import('../firebase');
  
  await updateDoc(doc(db, 'users', userId), {
    'integrations.googleCalendar': connected,
    'integrations.googleCalendarConnectedAt': connected ? new Date() : null
  });
}
